import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  getOppositionRejoinderConfirmation,
  saveOppositionRejoinderConfirmation,
  type OppositionRejoinderConfirmationItem,
} from "api/opposition";

export const OPPOSITION_CONFIRMATION_FOR_REJOINDER_FORM_ID =
  "opposition-confirmation-for-rejoinder-form";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

function YesNoToggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {(
        [
          { v: true, l: "YES" },
          { v: false, l: "NO" },
        ] as const
      ).map(({ v, l }) => (
        <button
          key={l}
          type="button"
          disabled={disabled}
          onClick={() => onChange(v)}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-[6px] text-xs font-medium transition-colors disabled:opacity-60 h-9 min-w-[88px] ${
            value === v
              ? "bg-[#222222] text-white"
              : "bg-white border border-[#EEEEEE] text-[#222222] hover:bg-[#F7F7F7]"
          }`}
        >
          <RadioCircle active={value === v} />
          {l}
        </button>
      ))}
    </div>
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionConfirmationFromClientForRejoinderView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [marks, setMarks] = useState<OppositionRejoinderConfirmationItem[]>([]);
  const [decisions, setDecisions] = useState<Record<string, boolean | null>>({});
  const [noOfApplicationsForRejoinder, setNoOfApplicationsForRejoinder] =
    useState<string>("");
  const [activeMarkIdx, setActiveMarkIdx] = useState(0);

  useEffect(() => {
    if (!leadId) {
      setMarks([]);
      setDecisions({});
      setNoOfApplicationsForRejoinder("");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionRejoinderConfirmation(leadId)
      .then((data) => {
        if (cancelled) return;
        setMarks(data.conflictingMarks);
        const prefill: Record<string, boolean | null> = {};
        data.conflictingMarks.forEach((m) => {
          prefill[m.id] = m.proceedWithRejoinder;
        });
        setDecisions(prefill);
        setNoOfApplicationsForRejoinder(
          data.noOfApplicationsForRejoinder != null
            ? String(data.noOfApplicationsForRejoinder)
            : ""
        );
        setActiveMarkIdx(0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load confirmation"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeMark = marks[activeMarkIdx];
  const allDecided = useMemo(
    () =>
      marks.length > 0 &&
      marks.every(
        (m) => decisions[m.id] === true || decisions[m.id] === false
      ),
    [marks, decisions]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (marks.length === 0) {
      toast.error(
        "No eligible applications. Mark evidenceFiledByConflictingProprietor=YES in Step 17 first."
      );
      return;
    }
    if (!allDecided) {
      toast.error("Select YES or NO for every application before continuing.");
      return;
    }
    let countOverride: number | null | undefined;
    const trimmed = noOfApplicationsForRejoinder.trim();
    if (trimmed === "") {
      countOverride = undefined;
    } else {
      const parsed = Number(trimmed);
      if (!Number.isInteger(parsed) || parsed < 0) {
        toast.error(
          "No. of applications for Rejoinder must be a non-negative integer."
        );
        return;
      }
      countOverride = parsed;
    }
    setSaving(true);
    try {
      await saveOppositionRejoinderConfirmation(leadId, {
        confirmations: marks.map((m) => ({
          conflictingMarkId: m.id,
          proceedWithRejoinder: decisions[m.id] as boolean,
        })),
        ...(countOverride !== undefined
          ? { noOfApplicationsForRejoinder: countOverride }
          : {}),
      });
      toast.success("Rejoinder confirmation saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save confirmation"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (marks.length === 0) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No eligible applications. Mark Evidence Filed by Conflicting
          Proprietor=YES in Step 17 first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_CONFIRMATION_FOR_REJOINDER_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {marks.map((m, idx) => {
            const isActive = idx === activeMarkIdx;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setActiveMarkIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(m.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-5 w-full">
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-xs font-normal text-[#222222] leading-5">
            Client Confirmation
          </span>
          <YesNoToggle
            value={activeMark ? decisions[activeMark.id] ?? null : null}
            onChange={(v) => {
              if (!activeMark) return;
              setDecisions((prev) => ({ ...prev, [activeMark.id]: v }));
            }}
            disabled={saving}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label
            htmlFor="opposition-rejoinder-no-of-applications"
            className="text-xs font-normal text-[#222222] leading-5"
          >
            No. of applications for Rejoinder
          </label>
          <input
            id="opposition-rejoinder-no-of-applications"
            type="number"
            min={0}
            step={1}
            placeholder="Text Here"
            value={noOfApplicationsForRejoinder}
            onChange={(e) => setNoOfApplicationsForRejoinder(e.target.value)}
            disabled={saving}
            className="w-full min-h-[44px] md:min-h-0 h-9 px-2.5 py-2 text-[12px] md:text-xs font-normal border border-[#EEEEEE] rounded-[6px] bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
          />
        </div>
      </div>
    </form>
  );
}
